import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import { projects } from '../data/projects';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #000000, #1a1a1a);
  color: white;
  padding: 3rem 0.5rem;
  
  @media (min-width: 768px) {
    padding: 6rem 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 0.5rem;
  
  @media (min-width: 768px) {
    padding: 0 1rem;
  }
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
  
  font-weight: 700;
  text-align: center;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
  
  background: linear-gradient(to right, #fff, #888);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.02em;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.75rem;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ProjectImage = styled.div`
  position: relative;
  aspect-ratio: 3/2;
  
  @media (min-width: 768px) {
    aspect-ratio: 16/9;
  }
  
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.8));
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
  
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.2;
`;

const ProjectDate = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const ProjectDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 1.25rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LearnMore = styled.div`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  transition: all 0.2s ease;

  svg {
    margin-left: 0.5rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: white;
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const Projects: React.FC = () => {
  return (
    <PageContainer>
      <ContentContainer>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Projects
        </Title>
        
        <ProjectGrid>
          {projects.map((project) => (
            <Link 
              key={project.id}
              to={`/projects/${project.id}`}
              style={{ textDecoration: 'none' }}
            >
              <ProjectCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                {project.coverImage ? (
                  <ProjectImage>
                    <img src={project.coverImage} alt={project.title} />
                  </ProjectImage>
                ) : (
                  <ProjectImage>
                    <img src="/placeholder-project.jpg" alt="Project placeholder" />
                  </ProjectImage>
                )}
                
                <ProjectContent>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDate>{project.date}</ProjectDate>
                  <ProjectDescription>
                    {project.description}
                  </ProjectDescription>
                  
                  <LearnMore>
                    <span>Learn more</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </LearnMore>
                </ProjectContent>
              </ProjectCard>
            </Link>
          ))}
        </ProjectGrid>
      </ContentContainer>
    </PageContainer>
  );
};

export default Projects; 